//文字コードはutf-8

import { LOADER } from '../utils/constants.js';

/**
 * CSSの読み込み完了を確認
 * @returns {Promise<boolean>} CSSが読み込まれているかどうか
 */
function checkCSSLoaded() {
  return new Promise((resolve) => {
    // すべてのスタイルシートが読み込まれているか確認
    const styleSheets = Array.from(document.styleSheets);
    
    // スタイルシートがない場合は即座に解決
    if (styleSheets.length === 0) {
      resolve(true);
      return;
    }

    let checkedCount = 0;
    const totalSheets = styleSheets.length;

    styleSheets.forEach((sheet, index) => {
      try {
        // スタイルシートが読み込まれているか確認（アクセス可能かどうか）
        const rules = sheet.cssRules || sheet.rules;
        // アクセス可能な場合は読み込み完了
        checkedCount++;
        if (checkedCount >= totalSheets) {
          resolve(true);
        }
      } catch (e) {
        // クロスオリジンのスタイルシートや読み込み中の場合はエラーになる
        // その場合は別の方法で確認
        if (sheet.href) {
          // link要素を探して確認
          const link = document.querySelector(`link[href="${sheet.href}"]`);
          if (link) {
            if (link.sheet) {
              // 読み込み完了
              checkedCount++;
              if (checkedCount >= totalSheets) {
                resolve(true);
              }
            } else {
              // 読み込み中
              link.addEventListener('load', () => {
                checkedCount++;
                if (checkedCount >= totalSheets) {
                  resolve(true);
                }
              }, { once: true });
              link.addEventListener('error', () => {
                // エラーでもカウント
                checkedCount++;
                if (checkedCount >= totalSheets) {
                  resolve(true);
                }
              }, { once: true });
            }
          } else {
            // link要素が見つからない場合は読み込み完了とみなす
            checkedCount++;
            if (checkedCount >= totalSheets) {
              resolve(true);
            }
          }
        } else {
          // hrefがない場合は読み込み完了とみなす
          checkedCount++;
          if (checkedCount >= totalSheets) {
            resolve(true);
          }
        }
      }
    });

    // タイムアウト（500ms経過したら読み込み完了とみなす）
    setTimeout(() => {
      resolve(true);
    }, 500);
  });
}

/**
 * DOMの構築とレンダリングが完了するまで待つ
 * @returns {Promise<void>}
 */
function waitForRenderComplete() {
  return new Promise((resolve) => {
    // DOMContentLoadedが既に発火している場合は即座に処理
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      // レンダリングが完了するまでrequestAnimationFrameを複数回実行
      // これにより、CSSが適用され、レイアウトが計算され、描画が完了するまで待つ
      let frameCount = 0;
      const maxFrames = 3; // 複数回実行して確実にレンダリング完了を待つ
      
      const checkRender = () => {
        frameCount++;
        if (frameCount >= maxFrames) {
          resolve();
        } else {
          requestAnimationFrame(checkRender);
        }
      };
      
      requestAnimationFrame(checkRender);
    } else {
      // DOMContentLoadedを待つ
      document.addEventListener('DOMContentLoaded', () => {
        // レンダリングが完了するまでrequestAnimationFrameを複数回実行
        let frameCount = 0;
        const maxFrames = 3;
        
        const checkRender = () => {
          frameCount++;
          if (frameCount >= maxFrames) {
            resolve();
          } else {
            requestAnimationFrame(checkRender);
          }
        };
        
        requestAnimationFrame(checkRender);
      }, { once: true });
    }
  });
}

/**
 * ページローダーの初期化
 * すべての画像、動画、CSS、HTMLレンダリングが完了したらフェードアウト
 */
export function initPageLoader() {
  const loader = document.querySelector(LOADER.SELECTOR);
  
  if (!loader) {
    return;
  }

  // ローディング中はスクロールをロック
  document.body.classList.add('scrollLock');
  
  // ローディング中はヘッダーを黒にする
  const header = document.querySelector('.header');
  if (header) {
    header.classList.add('is-black');
  }

  // 読み込み待ちの要素を収集
  const images = Array.from(document.querySelectorAll('img'));
  const videos = Array.from(document.querySelectorAll('video'));
  
  // 読み込み済みの要素を除外
  const pendingImages = images.filter(img => {
    // loading="lazy"の画像は除外（遅延読み込みのため）
    if (img.loading === 'lazy') {
      return false;
    }
    // 既に読み込まれている画像は除外
    return !img.complete || img.naturalWidth === 0;
  });
  
  const pendingVideos = videos.filter(video => {
    // data-ignore-loader が付いた動画や src を持たない動画は待機対象から除外
    const ignoreLoader = video.hasAttribute('data-ignore-loader');
    const hasSrcAttribute = Boolean(video.getAttribute('src'));
    const hasSourceChild = Boolean(video.querySelector('source'));
    const hasCurrentSrc = Boolean(video.currentSrc);
    const hasLoadableSource = hasSrcAttribute || hasSourceChild || hasCurrentSrc;

    // メタデータが読み込まれていない、かつ待機対象の動画のみ
    return !ignoreLoader && hasLoadableSource && video.readyState < 1;
  });

  // 読み込み完了フラグ
  let cssLoaded = false;
  let mediaLoaded = false;
  let renderComplete = false;

  // すべて読み込まれているかチェック
  const checkAllLoaded = () => {
    if (cssLoaded && mediaLoaded && renderComplete) {
      fadeOutLoader(loader);
    }
  };

  // HTMLのレンダリング完了を待つ
  waitForRenderComplete().then(() => {
    renderComplete = true;
    checkAllLoaded();
  });

  // CSSの読み込み完了を確認
  checkCSSLoaded().then(() => {
    cssLoaded = true;
    checkAllLoaded();
  });

  // 画像・動画の読み込み完了を確認
  if (pendingImages.length === 0 && pendingVideos.length === 0) {
    mediaLoaded = true;
    checkAllLoaded();
  } else {
    let loadedCount = 0;
    const totalCount = pendingImages.length + pendingVideos.length;
    
    // 画像の読み込み完了を監視
    pendingImages.forEach(img => {
      if (img.complete) {
        handleLoad();
      } else {
        img.addEventListener('load', handleLoad, { once: true });
        img.addEventListener('error', handleLoad, { once: true }); // エラー時もカウント
      }
    });

    // 動画の読み込み完了を監視（loadedmetadata 時点で完了とみなす）
    pendingVideos.forEach(video => {
      const handleLoadOnce = () => {
        if (video.dataset.yfcLoaderDone) {
          return;
        }
        video.dataset.yfcLoaderDone = '1';
        handleLoad();
      };

      // KV動画（#topKvVideo）の場合は、再生準備が完了するまで待つ
      const isKvVideo = video.id === 'topKvVideo';
      
      if (isKvVideo) {
        // KV動画の場合は、再生準備完了（yfcVideoReady）を待つ
        // 既に準備完了している場合は即座に処理
        if (video.dataset.yfcVideoReady === 'true') {
          handleLoadOnce();
        } else {
          // 再生準備完了を待つ（最大3秒）
          let videoReadyTimeout = null;
          const VIDEO_READY_TIMEOUT = 3000; // 3秒
          
          const handleVideoReady = () => {
            if (videoReadyTimeout) {
              clearTimeout(videoReadyTimeout);
              videoReadyTimeout = null;
            }
            handleLoadOnce();
          };
          
          // カスタムイベントを待つ
          video.addEventListener('yfcVideoReady', handleVideoReady, { once: true });
          // エラー時もカウント
          video.addEventListener('error', () => {
            if (videoReadyTimeout) {
              clearTimeout(videoReadyTimeout);
              videoReadyTimeout = null;
            }
            handleLoadOnce();
          }, { once: true });
          // バイパスイベントも処理
          video.addEventListener('yfcLoaderBypass', () => {
            if (videoReadyTimeout) {
              clearTimeout(videoReadyTimeout);
              videoReadyTimeout = null;
            }
            handleLoadOnce();
          }, { once: true });
          
          // タイムアウト処理（最大3秒）
          videoReadyTimeout = setTimeout(() => {
            videoReadyTimeout = null;
            // タイムアウト時は、メタデータが読み込まれていれば完了とみなす
            if (video.readyState >= 1) {
              handleLoadOnce();
            } else {
              // メタデータも読み込まれていない場合は、loadedmetadataを待つ
              video.addEventListener('loadedmetadata', handleLoadOnce, { once: true });
            }
          }, VIDEO_READY_TIMEOUT);
        }
      } else {
        // 通常の動画は従来通り loadedmetadata を待つ
        if (video.readyState >= 1) {
          handleLoadOnce();
        } else {
          // メタデータが読めた時点でローダー解除
          video.addEventListener('loadedmetadata', handleLoadOnce, { once: true });
          video.addEventListener('error', handleLoadOnce, { once: true }); // エラー時もカウント
          // 動画側から明示的にバイパスイベントが飛んできた場合も解除
          video.addEventListener('yfcLoaderBypass', handleLoadOnce, { once: true });
        }
      }
    });

    function handleLoad() {
      loadedCount++;
      if (loadedCount >= totalCount) {
        mediaLoaded = true;
        checkAllLoaded();
      }
    }
  }

  // タイムアウト処理（指定時間経過したら強制的にフェードアウト）
  setTimeout(() => {
    fadeOutLoader(loader);
  }, LOADER.TIMEOUT);
}

/**
 * ローダーをフェードアウト
 * @param {HTMLElement} loader - ローダー要素
 */
function fadeOutLoader(loader) {
  if (!loader || loader.classList.contains('is-fadeout')) {
    return;
  }

  loader.classList.add('is-fadeout');
  
  // スクロールロックを解除
  document.body.classList.remove('scrollLock');
  
  // ローディング完了後、ヘッダーの色判定を通常の処理に戻す
  // headerColorSwitchのupdateHeaderColorが呼ばれるように少し遅延させる
  setTimeout(() => {
    // カスタムイベントを発火してheaderColorSwitchに通知
    window.dispatchEvent(new CustomEvent('loaderFadeOut'));
  }, LOADER.FADEOUT_POST_DELAY);
  
  // アニメーション完了後に要素を非表示
  setTimeout(() => {
    loader.style.display = 'none';
  }, LOADER.FADEOUT_DELAY);
}
